import { useMemo } from 'react'
import { Button } from 'components/Button'
import { Footer } from 'components/Footer'
import { Header } from 'components/Header'
import { Layout } from 'components/Layout'
import { Link } from 'components/Link'
import { Stack } from 'components/Stack'
import { useFetchBlogTags } from 'features/Blog/api/queries/useFetchBlogTags'
import { PostCard, mapVariantsToIndex } from 'features/Blog/components/PostCard'
import { Tags } from 'features/Blog/components/Tags'
import { Loader } from 'features/components/Loader'
import { AllArticlesLinks } from './components/AllArticlesLinks'
import { BlogPagination } from './components/BlogPagination'
import { FeedArticle } from 'features/Blog/utils/parseFeed'
import { SearchableArticle } from 'features/Blog/utils/getBlogIndexPageProps'
import { pageHref } from 'features/Blog/utils/pageHref'
import { useTranslation } from 'hooks/useTranslation'
import { BlogHero } from './components/BlogHero'
import { PostRoot } from '../Post/Post.styles'
import { PostsListRoot, Wrapper } from './PostsList.styles'
import { Search } from 'components/Search'
import { searchItemProp } from 'components/Search/Search'
import { useRouter } from 'next/router'

type PostsListProps = {
  articles: FeedArticle[]
  page: number
  totalPages: number
  searchableArticles: SearchableArticle[]
  activeTags: string[]
}

export const PostsList = ({
  articles,
  page,
  totalPages,
  searchableArticles,
  activeTags,
}: PostsListProps) => {
  const { t } = useTranslation()
  const router = useRouter()

  const postEntries: searchItemProp[] = useMemo(
    () => searchableArticles.map((p) => ({ label: p.postTitle, value: p.postUrl })),
    [searchableArticles]
  )

  const { data: blogTags, status: blogTagsStatus } = useFetchBlogTags()

  if (blogTagsStatus === 'loading' || blogTagsStatus === 'error' || blogTagsStatus === 'idle')
    return <Loader />

  const CTA = (
    <Link href="/meal-plan" passHref>
      <Button as="a" className="header-cta" size="small">
        {t('blog:cta')}
      </Button>
    </Link>
  )

  const header = <Header snapToTop customCTA={CTA} />

  const handleSelect = (item: searchItemProp) => {
    router.push(`/blog/${item.value}`)
  }

  const handleTagsChange = (tags: string[]) => {
    router.push(pageHref(1, tags))
  }

  return (
    <PostRoot>
      <Layout header={header} CTA={{ text: 'blog:cta', url: '/' }}>
        <BlogHero />
        <Wrapper>
          {page === 1 && activeTags.length === 0 && (
            <AllArticlesLinks articles={searchableArticles} />
          )}
          <Stack direction="column" gap="7" css={{ maxWidth: '$max', margin: 'auto' }}>
            <Tags tags={blogTags.items} onChange={handleTagsChange} activeTags={activeTags} />
            <Search
              onSelect={handleSelect}
              placeholder="Search for topics here"
              items={postEntries}
              width="600px"
            />
            <div>
              <PostsListRoot>
                {articles.map((article, index) => (
                  <PostCard
                    key={article.slug}
                    article={article}
                    variant={mapVariantsToIndex[index % 4]}
                  />
                ))}
              </PostsListRoot>
            </div>
          </Stack>
          <BlogPagination page={page} totalPages={totalPages} tags={activeTags} />
        </Wrapper>
        <Footer />
      </Layout>
    </PostRoot>
  )
}
